import React, { useMemo } from 'react';
import styles from './HeroSection.module.css';
import logoImg from '../Logo/logo.png';
import { AnimatedShinyText } from './common/AnimatedShinyText';

/* ════════════════════════════════════════
   GRAIN TEXTURE (SVG feTurbulence)
   ════════════════════════════════════════ */
const grainSVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
  <filter id="grain">
    <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" stitchTiles="stitch"/>
  </filter>
  <rect width="200" height="200" filter="url(#grain)" opacity="1"/>
</svg>`;
const grainURL = `url("data:image/svg+xml,${encodeURIComponent(grainSVG)}")`;

/* ════════════════════════════════════════
   SMALL SVG ICONS
   ════════════════════════════════════════ */
const LockSVG = () => (
  <svg className={styles.lockIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* Dashboard nav icons */
const DashIcon = () => (
  <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
  </svg>
);
const ReportIcon = () => (
  <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);
const SettingsIcon = () => (
  <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);
const AdminIcon = () => (
  <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);


/* ════════════════════════════════════════
   ROTATING HEADLINES
   Shuffled deterministically by hour so all visitors
   see the same one, but it changes every 60 min.
   ════════════════════════════════════════ */
const HEADLINES = [
  {
    line1: 'Your testers use iPhones.',
    line2: 'Your users ',
    accent: "don't.",
  },
  {
    line1: 'Works in Bangalore.',
    line2: 'Crashes in ',
    accent: 'Bhagalpur.',
  },
  {
    line1: 'Your users found the bug first.',
    line2: "That's on ",
    accent: 'you.',
  },
  {
    line1: '68% of your users have 3GB RAM.',
    line2: "You've never tested on ",
    accent: 'one.',
  },
  {
    line1: 'QA on a MacBook Pro',
    line2: 'is not ',
    accent: 'QA.',
  },
  {
    line1: 'Test where your app',
    line2: 'actually ',
    accent: 'lives.',
  },
];

/* Simple seeded shuffle (Fisher-Yates with Park-Miller LCG) */
function seededShuffle(arr, seed) {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ════════════════════════════════════════
   AVATAR GRADIENTS
   ════════════════════════════════════════ */
const avatarGradients = [
  'linear-gradient(135deg, #ff8c28, #ff5a00)',
  'linear-gradient(135deg, #0ff08a, #00c853)',
  'linear-gradient(135deg, #7c3aed, #a855f7)',
  'linear-gradient(135deg, #3b82f6, #1d4ed8)',
  'linear-gradient(135deg, #ec4899, #be185d)',
];
const avatarInitials = ['SK', 'PR', 'AB', 'RJ', 'MK'];

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
export default function HeroSection({ onNavigate }) {

  /* Pick a headline based on the current hour */
  const headline = useMemo(() => {
    const now = new Date();
    const hourSeed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate() * 24 + now.getHours();
    const shuffled = seededShuffle(HEADLINES, hourSeed);
    return shuffled[0];
  }, []);

  const handleScrollToSampleReports = () => {
    const el = document.getElementById('sample-reports');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className={styles.hero}>

      {/* ── Grain texture ── */}
      <div
        className={styles.grain}
        style={{ backgroundImage: grainURL, backgroundRepeat: 'repeat', backgroundSize: '200px 200px' }}
      />

      {/* ── Background blobs ── */}
      <div className={`${styles.blob} ${styles.blob1}`} />
      <div className={`${styles.blob} ${styles.blob2}`} />
      <div className={`${styles.blob} ${styles.blob3}`} />

      {/* ── Grid lines ── */}
      <div className={styles.gridLines} />

      {/* ══ Two-column grid ══ */}
      <div className={styles.grid}>

        {/* ════ LEFT: Copy Column ════ */}
        <div className={styles.copyCol}>
          {/* Eyebrow pill */}
          <AnimatedShinyText className="hp-hero-badge" style={{ alignSelf: 'flex-start' }}>
            Real devices · Real India
          </AnimatedShinyText>

          {/* Headline — rotates every hour */}
          <h1 className={styles.headline}>
            {headline.line1}
            <br />
            {headline.line2}<span className={styles.headlineAccent}>{headline.accent}</span>
          </h1>

          {/* Subline */}
          <p className={styles.subline}>
            We put your app on ₹8k Samsungs with 2G, no storage, and regional keyboards. Real people break it. You get the report before your users do.
          </p>

          {/* CTA buttons */}
          <div className={styles.ctaRow}>
            <button className={styles.ctaPrimary} onClick={() => onNavigate && onNavigate('login')}>Start testing →</button>
            <button className={styles.ctaGhost} onClick={handleScrollToSampleReports}>See a real report</button>
          </div>

          {/* Trust row */}
          <div className={styles.trustRow}>
            <div className={styles.avatarStack}>
              {avatarGradients.map((bg, i) => (
                <div key={i} className={styles.avatar} style={{ background: bg, zIndex: 5 - i }}>
                  {avatarInitials[i]}
                </div>
              ))}
            </div>
            <span className={styles.trustText}>
              <strong>14 testers</strong> across 6 Indian cities
            </span>
          </div>
        </div>

        {/* ════ RIGHT: Mockup Column ════ */}
        <div className={styles.mockupCol}>


          {/* ══ Browser Frame ══ */}
          <div className={styles.browserFrame}>

            {/* Chrome bar */}
            <div className={styles.chromeBar}>
              <div className={`${styles.trafficLight} ${styles.tlRed}`} />
              <div className={`${styles.trafficLight} ${styles.tlAmber}`} />
              <div className={`${styles.trafficLight} ${styles.tlGreen}`} />
              <div className={styles.addressBar}>
                <div className={styles.addressPill}>
                  <LockSVG />
                  <span>bharatqa.fundup7.in/uclean</span>
                </div>
              </div>
            </div>

            {/* Dashboard crop */}
            <div className={styles.dashCrop}>
              <div className={styles.dashboard}>

                {/* ── Sidebar ── */}
                <div className={styles.dashSidebar}>
                  <div className={styles.dashBrand}>
                    <div className={styles.dashLogo}>
                      <img src={logoImg} alt="BharatQA" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                    </div>
                    <span className={styles.dashBrandName}>BharatQA</span>
                  </div>

                  <nav className={styles.dashNav}>
                    {[
                      { label: 'Overview', active: true, Icon: DashIcon },
                      { label: 'Bug Reports', active: false, Icon: ReportIcon },
                      { label: 'Devices', active: false, Icon: AdminIcon },
                      { label: 'Settings', active: false, Icon: SettingsIcon },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`${styles.dashNavItem} ${item.active ? styles.dashNavActive : ''}`}
                      >
                        <span className={`${styles.navDot} ${item.active ? styles.navDotActive : ''}`} />
                        <item.Icon />
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </nav>

                  <div className={styles.dashUserCard}>
                    <div className={styles.dashUserAvatar}>U</div>
                    <div className={styles.dashUserInfo}>
                      <span className={styles.dashUserName}>UClean</span>
                      <span className={styles.dashUserEmail}>dev@uclean.in</span>
                    </div>
                  </div>
                </div>

                {/* ── Main area: Bento Grid ── */}
                <div className={styles.dashMain}>

                  {/* Page header */}
                  <div className={styles.bentoHeader}>
                    <span className={styles.bentoTitle}>Overview</span>
                    <div className={styles.bentoControls}>
                      <span className={styles.bentoFilterBtn}>Past month</span>
                      <span className={styles.bentoPrimaryBtn}>Pull report</span>
                    </div>
                  </div>

                  {/* Bento grid */}
                  <div className={styles.bentoGrid}>

                    {/* Health Score (2-col) */}
                    <div className={`${styles.bentoCell} ${styles.bentoCellHealth} ${styles.bentoSpan2}`}>
                      <div className={styles.bentoCellLabel}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                        How stable is your app?
                      </div>
                      <div className={styles.healthRow}>
                        <div className={`${styles.healthNumber} ${styles.healthCritical}`}>32<span className={styles.healthDenom}>/100</span></div>
                        <div className={styles.healthMeta}>
                          <div className={`${styles.healthStatus} ${styles.healthStatusCritical}`}><span className={`${styles.healthDot} ${styles.healthDotCritical}`} />Critical</div>
                          <p className={styles.healthDesc}>
                            App is bleeding users. Critical checkout failures detected on ₹10k devices.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats (1-col) */}
                    <div className={styles.bentoCell}>
                      <div className={styles.bentoCellLabel}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        This month
                      </div>
                      <div className={styles.quickStats}>
                        <div className={styles.statItem}><span className={styles.statLabel}>Test sessions</span><span className={styles.statValue}>12.4k <span className={styles.trendUp}>+18%</span></span></div>
                        <div className={styles.statItem}><span className={styles.statLabel}>Stable runs</span><span className={styles.statValue}>2.1k <span className={styles.trendDown}>-72%</span></span></div>
                        <div className={styles.statItem}><span className={styles.statLabel}>Critical bugs</span><span className={styles.statValue}>812 <span className={styles.trendDown}>+142%</span></span></div>
                      </div>
                    </div>

                    {/* Device Tier (1-col) */}
                    <div className={styles.bentoCell}>
                      <div className={styles.bentoCellLabel}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 116 0v1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 014-4h4a4 4 0 014 4v3c0 3.3-2.7 6-6 6z"/></svg>
                        Where bugs live
                      </div>
                      <div className={styles.distList}>
                        <div className={styles.distRow}><span className={styles.distName}>Budget</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'68%',background:'#C44B3B'}} /></div><span className={styles.distVal}>68%</span></div>
                        <div className={styles.distRow}><span className={styles.distName}>Mid-range</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'24%',background:'#C4893B'}} /></div><span className={styles.distVal}>24%</span></div>
                        <div className={styles.distRow}><span className={styles.distName}>Flagship</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'8%',background:'#5B7FA6'}} /></div><span className={styles.distVal}>8%</span></div>
                      </div>
                    </div>

                    {/* OS Distribution (2-col) */}
                    <div className={`${styles.bentoCell} ${styles.bentoSpan2}`}>
                      <div className={styles.bentoCellLabel}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                        OS breakdown
                      </div>
                      <div className={styles.distList}>
                        <div className={styles.distRow}><span className={styles.distName}>Android 13</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'45%',background:'#5B7FA6'}} /></div><span className={styles.distVal}>45.2%</span></div>
                        <div className={styles.distRow}><span className={styles.distName}>Android 12</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'32%',background:'#4A9B6E'}} /></div><span className={styles.distVal}>32.8%</span></div>
                        <div className={styles.distRow}><span className={styles.distName}>Android 11</span><div className={styles.distTrack}><div className={styles.distFill} style={{width:'22%',background:'#C4893B'}} /></div><span className={styles.distVal}>22.0%</span></div>
                      </div>
                    </div>

                    {/* Activity Chart (3-col) */}
                    <div className={`${styles.bentoCell} ${styles.bentoSpan3}`}>
                      <div className={styles.chartHeader}>
                        <div className={styles.bentoCellLabel} style={{marginBottom:0}}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                          Activity
                        </div>
                        <div className={styles.chartLegend}>
                          <span className={styles.legendItem}><span className={styles.legendDot} style={{background:'#5B7FA6'}} /> Sessions</span>
                          <span className={styles.legendItem}><span className={styles.legendDot} style={{background:'#4A9B6E'}} /> Stable</span>
                          <span className={styles.legendItem}><span className={styles.legendDot} style={{background:'#C44B3B'}} /> Bugs</span>
                        </div>
                      </div>
                      <div className={styles.chartArea}>
                        <svg viewBox="0 0 500 110" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="heroAreaFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#5B7FA6" stopOpacity="1" />
                              <stop offset="100%" stopColor="#5B7FA6" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <line x1="20" y1="10" x2="490" y2="10" className={styles.chartGrid} />
                          <line x1="20" y1="35" x2="490" y2="35" className={styles.chartGrid} />
                          <line x1="20" y1="60" x2="490" y2="60" className={styles.chartGrid} />
                          <line x1="20" y1="85" x2="490" y2="85" className={styles.chartGrid} />
                          <polygon points="50,72 160,52 270,44 380,24 470,18 470,85 50,85" className={styles.chartAreaFill} />
                          <polyline points="50,72 160,52 270,44 380,24 470,18" className={styles.chartLineSessions} />
                          <polyline points="50,76 160,62 270,54 380,32 470,28" className={styles.chartLineStable} />
                          <polyline points="50,82 160,80 270,79 380,82 470,81" className={styles.chartLineBugs} />
                          <line x1="270" y1="10" x2="270" y2="85" className={styles.chartMarkerLine} />
                          <circle cx="270" cy="44" r="3" className={styles.chartMarkerDot} />
                        </svg>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* end browser frame */}
        </div>
        {/* end mockup col */}
      </div>
    </section>
  );
}
